// PasskeyCompatibilityChecker Component
// Checks and displays browser/device compatibility with passkeys

import React, { useState, useEffect } from 'react';
import { WebAuthnUtils } from '../lib/webauthn-utils';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Info,
  Smartphone,
  Monitor,
  Usb,
  Fingerprint,
  Shield,
  Zap,
  RefreshCw
} from 'lucide-react';
import type { WebAuthnSupport } from '../types/webauthn';

interface PasskeyCompatibilityCheckerProps {
  onSupportChange?: (isSupported: boolean) => void;
  showDetails?: boolean;
  className?: string;
}

const PasskeyCompatibilityChecker: React.FC<PasskeyCompatibilityCheckerProps> = ({
  onSupportChange,
  showDetails = true,
  className = ''
}) => {
  const [support, setSupport] = useState<WebAuthnSupport | null>(null);
  const [loading, setLoading] = useState(true);
  const [userAgent, setUserAgent] = useState('');

  // Check support on mount
  useEffect(() => {
    checkSupport();
    setUserAgent(navigator.userAgent);
  }, []);

  // Notify parent of support changes
  useEffect(() => {
    if (support && onSupportChange) {
      onSupportChange(support.available);
    }
  }, [support, onSupportChange]);

  const checkSupport = async () => {
    setLoading(true);
    try {
      const supportInfo = await WebAuthnUtils.WebAuthnDetector.checkSupport();
      setSupport(supportInfo);
    } catch (error) {
      console.error('Error checking WebAuthn support:', error);
      setSupport({
        available: false,
        platform_authenticator: false,
        cross_platform_authenticator: false,
        user_verification: false,
        resident_key: false,
        conditional_mediation: false,
        large_blob: false
      });
    } finally {
      setLoading(false);
    }
  };

  // Get browser info
  const getBrowserInfo = () => {
    const ua = userAgent.toLowerCase();
    if (ua.includes('chrome')) return { name: 'Chrome', supported: true };
    if (ua.includes('firefox')) return { name: 'Firefox', supported: true };
    if (ua.includes('safari') && !ua.includes('chrome')) return { name: 'Safari', supported: true };
    if (ua.includes('edge')) return { name: 'Edge', supported: true };
    return { name: 'Desconhecido', supported: false };
  };

  // Get OS info
  const getOSInfo = () => {
    const ua = userAgent.toLowerCase();
    if (ua.includes('windows')) return { name: 'Windows', icon: <Monitor className="h-4 w-4" /> };
    if (ua.includes('mac')) return { name: 'macOS', icon: <Monitor className="h-4 w-4" /> };
    if (ua.includes('iphone') || ua.includes('ipad')) return { name: 'iOS', icon: <Smartphone className="h-4 w-4" /> };
    if (ua.includes('android')) return { name: 'Android', icon: <Smartphone className="h-4 w-4" /> };
    if (ua.includes('linux')) return { name: 'Linux', icon: <Monitor className="h-4 w-4" /> };
    return { name: 'Desconhecido', icon: <Monitor className="h-4 w-4" /> };
  };

  const browserInfo = getBrowserInfo();
  const osInfo = getOSInfo();

  if (loading) {
    return (
      <Card className={`p-4 ${className}`}>
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span>Verificando compatibilidade...</span>
        </div>
      </Card>
    );
  }

  if (!support) {
    return (
      <Card className={`p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <XCircle className="h-5 w-5 text-red-500" />
            <span>Erro ao verificar compatibilidade</span>
          </div>
          <Button size="sm" variant="outline" onClick={checkSupport}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Main Status */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {support.available ? (
              <CheckCircle className="h-6 w-6 text-green-500" />
            ) : (
              <XCircle className="h-6 w-6 text-red-500" />
            )}
            <div>
              <h3 className="font-semibold">
                {support.available ? 'Passkeys Suportados' : 'Passkeys Não Suportados'}
              </h3>
              <p className="text-sm text-gray-600">
                {support.available 
                  ? 'Seu dispositivo e navegador suportam passkeys'
                  : 'Seu dispositivo ou navegador não suporta passkeys'
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Badge 
              variant={support.available ? 'default' : 'destructive'}
              className="flex items-center space-x-1"
            >
              <Shield className="h-3 w-3" />
              <span>{support.available ? 'Compatível' : 'Incompatível'}</span>
            </Badge>
            
            <Button size="sm" variant="outline" onClick={checkSupport}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Detailed Information */}
      {showDetails && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Browser & OS Info */}
          <Card className="p-4">
            <h4 className="font-semibold mb-3 flex items-center space-x-2">
              <Info className="h-4 w-4" />
              <span>Informações do Sistema</span>
            </h4>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Navegador:</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium">{browserInfo.name}</span>
                  {browserInfo.supported ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Sistema:</span>
                <div className="flex items-center space-x-2">
                  {osInfo.icon}
                  <span className="text-sm font-medium">{osInfo.name}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Feature Support */}
          <Card className="p-4">
            <h4 className="font-semibold mb-3 flex items-center space-x-2">
              <Fingerprint className="h-4 w-4" />
              <span>Recursos Disponíveis</span>
            </h4>
            
            <div className="space-y-2">
              <FeatureItem 
                icon={<Smartphone className="h-4 w-4" />}
                label="Autenticador Integrado"
                supported={support.platform_authenticator}
                description="Touch ID, Face ID, Windows Hello"
              />
              
              <FeatureItem 
                icon={<Usb className="h-4 w-4" />}
                label="Autenticador Externo"
                supported={support.cross_platform_authenticator}
                description="YubiKey, chaves de segurança USB"
              />
              
              <FeatureItem 
                icon={<Shield className="h-4 w-4" />}
                label="Verificação de Usuário"
                supported={support.user_verification}
                description="Biometria ou PIN"
              />
              
              <FeatureItem 
                icon={<Zap className="h-4 w-4" />}
                label="Preenchimento Automático"
                supported={support.conditional_mediation}
                description="Login automático em formulários"
              />
            </div>
          </Card>
        </div>
      )}

      {/* Recommendations */}
      {!support.available && (
        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-yellow-900 mb-2">Como habilitar passkeys</h4>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>• Atualize seu navegador para a versão mais recente</li>
                <li>• Use Chrome 67+, Firefox 60+, Safari 14+ ou Edge 18+</li>
                <li>• Certifique-se de que está acessando via HTTPS</li>
                <li>• Verifique se seu dispositivo tem autenticador (biometria, PIN)</li>
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Advanced Features */}
      {support.available && showDetails && (
        <Card className="p-4">
          <h4 className="font-semibold mb-3">Recursos Avançados</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FeatureItem 
              icon={<Shield className="h-4 w-4" />}
              label="Chaves Residentes"
              supported={support.resident_key}
              description="Passkeys armazenados no dispositivo"
            />
            
            <FeatureItem 
              icon={<Info className="h-4 w-4" />}
              label="Large Blob Extension"
              supported={support.large_blob}
              description="Armazenamento de dados adicionais"
            />
          </div>
        </Card>
      )}
    </div>
  );
};

// Helper component for feature items
interface FeatureItemProps {
  icon: React.ReactNode;
  label: string;
  supported: boolean;
  description?: string;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ icon, label, supported, description }) => (
  <div className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
    <div className="flex items-center space-x-3">
      <div className={`${supported ? 'text-blue-600' : 'text-gray-400'}`}>
        {icon}
      </div>
      <div>
        <div className="text-sm font-medium">{label}</div>
        {description && (
          <div className="text-xs text-gray-500">{description}</div>
        )}
      </div>
    </div>
    
    {supported ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <XCircle className="h-4 w-4 text-gray-400" />
    )}
  </div>
);

export default PasskeyCompatibilityChecker;